import { useState, useMemo, useEffect } from 'react';
import { X, Plus, Trash2, Filter, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useFilters } from '@/contexts/FilterContext';
import { AdvancedFilterCondition, AdvancedFilterOperator, AdvancedFilterField, AdvancedFilters } from '@/types/filters';
import { Booking } from '@/types';

// Define all available booking fields for advanced filtering
const BOOKING_FIELDS: AdvancedFilterField[] = [
  // Core Fields
  { fieldName: 'description', displayName: 'Description', fieldType: 'text', operators: ['equals', 'not-equals', 'contains', 'not-contains', 'starts-with', 'ends-with', 'is-empty', 'is-not-empty'] },
  { fieldName: 'projectName', displayName: 'Project Name', fieldType: 'text', operators: ['equals', 'not-equals', 'contains', 'not-contains', 'starts-with', 'ends-with', 'is-empty', 'is-not-empty'] },
  { fieldName: 'taskDescription', displayName: 'Task Description', fieldType: 'text', operators: ['equals', 'not-equals', 'contains', 'not-contains', 'starts-with', 'ends-with', 'is-empty', 'is-not-empty'] },
  { fieldName: 'bookingNotes', displayName: 'Booking Notes', fieldType: 'text', operators: ['equals', 'not-equals', 'contains', 'not-contains', 'starts-with', 'ends-with', 'is-empty', 'is-not-empty'] },
  { fieldName: 'bookingReference', displayName: 'Booking Reference', fieldType: 'text', operators: ['equals', 'not-equals', 'contains', 'not-contains', 'starts-with', 'ends-with', 'is-empty', 'is-not-empty'] },
  { fieldName: 'customer', displayName: 'Customer', fieldType: 'text', operators: ['equals', 'not-equals', 'contains', 'not-contains', 'starts-with', 'ends-with', 'is-empty', 'is-not-empty'] },
  { fieldName: 'location', displayName: 'Location', fieldType: 'text', operators: ['equals', 'not-equals', 'contains', 'not-contains', 'starts-with', 'ends-with', 'is-empty', 'is-not-empty'] },
  { fieldName: 'details', displayName: 'Details', fieldType: 'text', operators: ['equals', 'not-equals', 'contains', 'not-contains', 'starts-with', 'ends-with', 'is-empty', 'is-not-empty'] },
  { fieldName: 'costCentreTask', displayName: 'Cost Centre Task', fieldType: 'text', operators: ['equals', 'not-equals', 'contains', 'not-contains', 'starts-with', 'ends-with', 'is-empty', 'is-not-empty'] },
  
  // Date/Time Fields
  { fieldName: 'startTime', displayName: 'Start Date/Time', fieldType: 'datetime', operators: ['equals', 'not-equals', 'greater-than', 'less-than', 'between'] },
  { fieldName: 'endTime', displayName: 'End Date/Time', fieldType: 'datetime', operators: ['equals', 'not-equals', 'greater-than', 'less-than', 'between'] },
  
  // Enum Fields
  { 
    fieldName: 'durationType', 
    displayName: 'Duration Type', 
    fieldType: 'select', 
    operators: ['equals', 'not-equals', 'in', 'not-in'],
    options: [
      { value: 'full-day', label: 'Full Day' },
      { value: 'half-day', label: 'Half Day' },
      { value: 'half-day-am', label: 'Half Day (AM)' },
      { value: 'half-day-pm', label: 'Half Day (PM)' },
      { value: 'hourly', label: 'Hourly' },
    ]
  },
  { 
    fieldName: 'billingClassification', 
    displayName: 'Billing Classification', 
    fieldType: 'select', 
    operators: ['equals', 'not-equals', 'in', 'not-in'],
    options: [
      { value: 'billable', label: 'Billable' },
      { value: 'non-billable', label: 'Non-Billable' },
      { value: 'internal', label: 'Internal' },
    ]
  },
  { 
    fieldName: 'workType', 
    displayName: 'Work Type', 
    fieldType: 'select', 
    operators: ['equals', 'not-equals', 'in', 'not-in'],
    options: [
      { value: 'onsite', label: 'Onsite' },
      { value: 'remote', label: 'Remote' },
      { value: 'hybrid', label: 'Hybrid' },
    ]
  },
  { 
    fieldName: 'status', 
    displayName: 'Status', 
    fieldType: 'select', 
    operators: ['equals', 'not-equals', 'in', 'not-in'],
    options: [
      { value: 'planned', label: 'Planned' },
      { value: 'confirmed', label: 'Confirmed' },
      { value: 'cancelled', label: 'Cancelled' },
      { value: 'completed', label: 'Completed' },
    ]
  },
  { 
    fieldName: 'priority', 
    displayName: 'Priority', 
    fieldType: 'select', 
    operators: ['equals', 'not-equals', 'in', 'not-in'],
    options: [
      { value: 'high', label: 'High' },
      { value: 'medium', label: 'Medium' },
      { value: 'low', label: 'Low' },
    ]
  },
  
  // Boolean Fields
  { fieldName: 'notifyReminders', displayName: 'Notify Reminders', fieldType: 'boolean', operators: ['equals'] },
  
  // User-Defined Fields (User Char 1-20)
  ...Array.from({ length: 20 }, (_, i) => ({
    fieldName: `userChar.${i + 1}`,
    displayName: `User Char ${i + 1}`,
    fieldType: 'text' as const,
    operators: ['equals', 'not-equals', 'contains', 'not-contains', 'starts-with', 'ends-with', 'is-empty', 'is-not-empty'] as AdvancedFilterOperator[],
  })),
  
  // User Number 1-10
  ...Array.from({ length: 10 }, (_, i) => ({
    fieldName: `userNumber.${i + 1}`,
    displayName: `User Number ${i + 1}`,
    fieldType: 'number' as const,
    operators: ['equals', 'not-equals', 'greater-than', 'less-than', 'between', 'is-empty', 'is-not-empty'] as AdvancedFilterOperator[],
  })),
  
  // User Flag 1-10
  ...Array.from({ length: 10 }, (_, i) => ({
    fieldName: `userFlag.${i + 1}`,
    displayName: `User Flag ${i + 1}`,
    fieldType: 'boolean' as const,
    operators: ['equals'] as AdvancedFilterOperator[],
  })),
  
  // User Date 1-10
  ...Array.from({ length: 10 }, (_, i) => ({
    fieldName: `userDate.${i + 1}`,
    displayName: `User Date ${i + 1}`,
    fieldType: 'date' as const,
    operators: ['equals', 'not-equals', 'greater-than', 'less-than', 'between', 'is-empty', 'is-not-empty'] as AdvancedFilterOperator[],
  })),
  
  // User Notes 1-5
  ...Array.from({ length: 5 }, (_, i) => ({
    fieldName: `userNotes.${i + 1}`,
    displayName: `User Notes ${i + 1}`,
    fieldType: 'text' as const,
    operators: ['equals', 'not-equals', 'contains', 'not-contains', 'starts-with', 'ends-with', 'is-empty', 'is-not-empty'] as AdvancedFilterOperator[],
  })),
  
  // User Time 1-5
  ...Array.from({ length: 5 }, (_, i) => ({
    fieldName: `userTime.${i + 1}`,
    displayName: `User Time ${i + 1}`,
    fieldType: 'time' as const,
    operators: ['equals', 'not-equals', 'greater-than', 'less-than', 'between', 'is-empty', 'is-not-empty'] as AdvancedFilterOperator[],
  })),
];

const OPERATOR_LABELS: Record<AdvancedFilterOperator, string> = {
  'equals': 'Equals',
  'not-equals': 'Not Equals',
  'contains': 'Contains',
  'not-contains': 'Does Not Contain',
  'starts-with': 'Starts With',
  'ends-with': 'Ends With',
  'greater-than': 'Greater Than',
  'less-than': 'Less Than',
  'between': 'Between',
  'is-empty': 'Is Empty',
  'is-not-empty': 'Is Not Empty',
  'in': 'In',
  'not-in': 'Not In',
};

interface AdvancedFilterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  draftAdvancedFilters?: AdvancedFilters;
  onDraftApply?: (filters: AdvancedFilters) => void;
}

export function AdvancedFilterDialog({ open, onOpenChange, draftAdvancedFilters, onDraftApply }: AdvancedFilterDialogProps) {
  const { advancedFilters, setAdvancedFilters, resetAdvancedFilters } = useFilters();

  const sourceFilters = draftAdvancedFilters ?? advancedFilters;

  const [localConditions, setLocalConditions] = useState<AdvancedFilterCondition[]>(sourceFilters.conditions);
  const [localLogicOperator, setLocalLogicOperator] = useState<'AND' | 'OR'>(sourceFilters.logicOperator);

  useEffect(() => {
    if (open) {
      setLocalConditions(sourceFilters.conditions);
      setLocalLogicOperator(sourceFilters.logicOperator);
    }
  }, [open, sourceFilters]);

  const addCondition = () => {
    const newCondition: AdvancedFilterCondition = {
      id: `condition-${Date.now()}`,
      fieldName: 'description',
      operator: 'contains',
      value: '',
    };
    setLocalConditions([...localConditions, newCondition]);
  };

  const removeCondition = (id: string) => {
    setLocalConditions(localConditions.filter(c => String(c.id) !== String(id)));
  };

  const updateCondition = (id: string, updates: Partial<AdvancedFilterCondition>) => {
    setLocalConditions(localConditions.map(c => 
      String(c.id) === String(id) ? { ...c, ...updates } : c
    ));
  };

  const handleApply = () => {
    const newFilters: AdvancedFilters = {
      conditions: localConditions,
      logicOperator: localLogicOperator,
    };
    if (onDraftApply) {
      onDraftApply(newFilters);
    } else {
      setAdvancedFilters(newFilters);
    }
    onOpenChange(false);
  };

  const handleClear = () => {
    setLocalConditions([]);
    setLocalLogicOperator('AND');
    if (onDraftApply) {
      onDraftApply({ conditions: [], logicOperator: 'AND' });
    } else {
      resetAdvancedFilters();
    }
  };

  const handleCancel = () => {
    setLocalConditions(sourceFilters.conditions);
    setLocalLogicOperator(sourceFilters.logicOperator);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Advanced Filters
          </DialogTitle>
          <DialogDescription>
            Create complex filter conditions to refine your booking timeline view. Combine multiple conditions using AND/OR logic.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[400px]">
          <ScrollAreaViewport className="h-full">
            <div className="space-y-4 pr-4">
              {/* Logic Operator Selection */}
              {localConditions.length > 1 && (
                <div className="space-y-2">
                  <Label>Logic Operator</Label>
                  <RadioGroup
                    value={localLogicOperator}
                    onValueChange={(value) => setLocalLogicOperator(value as 'AND' | 'OR')}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="AND" id="and" />
                      <Label htmlFor="and" className="cursor-pointer">
                        AND (All conditions must match)
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="OR" id="or" />
                      <Label htmlFor="or" className="cursor-pointer">
                        OR (Any condition can match)
                      </Label>
                    </div>
                  </RadioGroup>
                  <Separator />
                </div>
              )}

              {/* Conditions List */}
              {localConditions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Filter className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No filter conditions added yet.</p>
                  <p className="text-sm">Click "Add Condition" to get started.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {localConditions.map((condition, index) => (
                    <ConditionRow
                      key={condition.id}
                      condition={condition}
                      index={index}
                      onUpdate={(updates) => updateCondition(condition.id, updates)}
                      onRemove={() => removeCondition(condition.id)}
                    />
                  ))}
                </div>
              )}

              {/* Add Condition Button */}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addCondition}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Condition
              </Button>
            </div>
          </ScrollAreaViewport>
          <ScrollAreaScrollbar orientation="vertical" />
        </ScrollArea>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClear} disabled={localConditions.length === 0}>
            Clear All
          </Button>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleApply}>
            <Save className="h-4 w-4 mr-2" />
            Apply Filters
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface ConditionRowProps {
  condition: AdvancedFilterCondition;
  index: number;
  onUpdate: (updates: Partial<AdvancedFilterCondition>) => void;
  onRemove: () => void;
}

function ConditionRow({ condition, index, onUpdate, onRemove }: ConditionRowProps) {
  const [fieldSearch, setFieldSearch] = useState('');
  const selectedField = BOOKING_FIELDS.find(f => f.fieldName === condition.fieldName);
  const availableOperators = selectedField?.operators || [];
  const needsValue = !['is-empty', 'is-not-empty'].includes(condition.operator);
  const needsSecondValue = condition.operator === 'between';

  // Filter fields based on search
  const filteredFields = useMemo(() => {
    if (!fieldSearch.trim()) return BOOKING_FIELDS;
    const search = fieldSearch.toLowerCase();
    return BOOKING_FIELDS.filter(field => 
      field.displayName.toLowerCase().includes(search)
    );
  }, [fieldSearch]);

  return (
    <div className="flex items-start gap-2 p-3 border rounded-lg bg-muted/30">
      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-medium flex-shrink-0 mt-1">
        {index + 1}
      </div>

      <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-2">
        {/* Field Selection */}
        <div className="space-y-1">
          <Label className="text-xs">Field</Label>
          <Select
            value={condition.fieldName}
            onValueChange={(value) => {
              const field = BOOKING_FIELDS.find(f => f.fieldName === value);
              onUpdate({
                fieldName: value,
                operator: field?.operators[0] || 'equals',
                value: '',
                value2: undefined,
              });
              setFieldSearch(''); // Clear search on selection
            }}
            onOpenChange={(open) => {
              if (!open) setFieldSearch(''); // Clear search when dropdown closes
            }}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-[320px]">
              {/* Search Input */}
              <div className="sticky top-0 z-10 bg-popover p-2 border-b">
                <Input
                  placeholder="Search fields..."
                  value={fieldSearch}
                  onChange={(e) => setFieldSearch(e.target.value)}
                  className="h-8"
                  onClick={(e) => e.stopPropagation()} // Prevent dropdown close on click
                  onKeyDown={(e) => e.stopPropagation()} // Prevent dropdown navigation
                />
              </div>
              
              {/* Scrollable Field List */}
              <div className="max-h-[240px] overflow-y-auto">
                {filteredFields.length > 0 ? (
                  filteredFields.map((field) => (
                    <SelectItem key={field.fieldName} value={field.fieldName}>
                      {field.displayName}
                    </SelectItem>
                  ))
                ) : (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    No fields found
                  </div>
                )}
              </div>
            </SelectContent>
          </Select>
        </div>

        {/* Operator Selection */}
        <div className="space-y-1">
          <Label className="text-xs">Operator</Label>
          <Select
            value={condition.operator}
            onValueChange={(value) => onUpdate({ operator: value as AdvancedFilterOperator })}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableOperators.map((op) => (
                <SelectItem key={op} value={op}>
                  {OPERATOR_LABELS[op]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Value Input(s) */}
        {needsValue && (
          <div className="space-y-1 md:col-span-2">
            <Label className="text-xs">Value{needsSecondValue && ' (From)'}</Label>
            <div className="flex gap-2">
              {selectedField?.fieldType === 'boolean' ? (
                <Select
                  value={String(condition.value)}
                  onValueChange={(value) => onUpdate({ value: value === 'true' })}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">True</SelectItem>
                    <SelectItem value="false">False</SelectItem>
                  </SelectContent>
                </Select>
              ) : selectedField?.fieldType === 'select' ? (
                <Select
                  value={condition.value}
                  onValueChange={(value) => onUpdate({ value })}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedField.options?.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  type={selectedField?.fieldType === 'number' ? 'number' : selectedField?.fieldType === 'date' ? 'date' : selectedField?.fieldType === 'datetime' ? 'datetime-local' : selectedField?.fieldType === 'time' ? 'time' : 'text'}
                  value={condition.value || ''}
                  onChange={(e) => onUpdate({ value: e.target.value })}
                  placeholder={selectedField?.placeholder || 'Enter value...'}
                  className="h-9"
                />
              )}
              
              {needsSecondValue && (
                <>
                  <span className="flex items-center text-muted-foreground">to</span>
                  <Input
                    type={selectedField?.fieldType === 'number' ? 'number' : selectedField?.fieldType === 'date' ? 'date' : selectedField?.fieldType === 'datetime' ? 'datetime-local' : selectedField?.fieldType === 'time' ? 'time' : 'text'}
                    value={condition.value2 || ''}
                    onChange={(e) => onUpdate({ value2: e.target.value })}
                    placeholder="To value..."
                    className="h-9"
                  />
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onRemove}
        className="h-9 w-9 flex-shrink-0 mt-6"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}
